/**
 * Collection Complete Execution Results — typing, labels, and grouping
 * (Round Robin: by iteration / by data-set row / flat).
 */

import { isAutoGeneratedDataSetName } from './executionDataSetIdentity';

export type CollectionExecutionMode = 'roundRobinDataSet' | 'requestByRequest';

export type CollectionExecutionResultsViewMode = 'flat' | 'byIteration' | 'byDataSetRow';

export type CollectionExecutionReportDataSetRef = {
  id: number;
  name: string;
};

export type CollectionExecutionReportResultRowBase = {
  success: boolean;
  iteration?: number;
  dataSetRoundIndex?: number | null;
  /** 1-based configured order index (stable across parallel completion). */
  dataSetOrder?: number | null;
  /** Display label bound at execution time, e.g. "Data Set 2". */
  dataSetLabel?: string | null;
  dataSet?: CollectionExecutionReportDataSetRef | null;
};

export function formatExecutionDataSetRef(
  dataSet: { id?: number | null; name?: string | null } | null | undefined
): CollectionExecutionReportDataSetRef | null {
  if (dataSet == null || dataSet.id == null) return null;
  const id = Number(dataSet.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const name = (dataSet.name && String(dataSet.name).trim()) || `Data Set ${id}`;
  return { id, name };
}

/**
 * Display label for the Data Set column in execution reports.
 * Prefer execution-bound metadata over stale stored names or database ids.
 */
export function getExecutionDataSetLabel(
  row: CollectionExecutionReportResultRowBase
): string {
  const boundLabel = row.dataSetLabel?.trim();
  if (boundLabel) return boundLabel;

  const hasDataSetId = row.dataSet?.id != null && Number(row.dataSet.id) > 0;

  const orderFromField =
    row.dataSetOrder != null && Number.isFinite(Number(row.dataSetOrder))
      ? Math.max(1, Math.floor(Number(row.dataSetOrder)))
      : null;
  const orderFromRound =
    hasDataSetId &&
    row.dataSetRoundIndex != null &&
    Number.isFinite(Number(row.dataSetRoundIndex))
      ? Math.max(1, Math.floor(Number(row.dataSetRoundIndex)) + 1)
      : null;
  const configuredOrder = orderFromField ?? orderFromRound;

  if (configuredOrder != null && hasDataSetId) {
    const storedName = row.dataSet?.name?.trim();
    if (storedName && !isAutoGeneratedDataSetName(storedName)) {
      return storedName;
    }
    return `Data Set ${configuredOrder}`;
  }

  if (row.dataSet != null) {
    const name = row.dataSet.name?.trim();
    if (name) return name;
    const id = Number(row.dataSet.id);
    if (Number.isFinite(id) && id > 0) return `Data Set ${id}`;
  }
  return 'No Data Set';
}

export function summarizeExecutionResultRows(rows: Array<{ success: boolean }>): {
  total: number;
  passed: number;
  failed: number;
} {
  const total = rows.length;
  const passed = rows.filter((r) => r.success).length;
  return { total, passed, failed: total - passed };
}

export function uniqueDataSetNamesInRows(
  rows: Array<{ dataSet?: CollectionExecutionReportDataSetRef | null }>
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const name = row.dataSet?.name?.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

export function formatDataSetRowGroupLabel(
  dataSetRoundIndex: number | null,
  rows: Array<{ dataSet?: CollectionExecutionReportDataSetRef | null }>
): string {
  const names = uniqueDataSetNamesInRows(rows);
  if (names.length === 1) return names[0];
  if (names.length > 1) return names.join(', ');
  if (dataSetRoundIndex == null) return 'No Data Set';
  return `Data Set ${dataSetRoundIndex + 1}`;
}

export type DataSetRowGroup<T extends CollectionExecutionReportResultRowBase> = {
  dataSetRoundIndex: number | null;
  label: string;
  results: T[];
  total: number;
  passed: number;
  failed: number;
};

export type IterationGroup<T extends CollectionExecutionReportResultRowBase> = {
  iteration: number;
  results: T[];
  total: number;
  passed: number;
  failed: number;
  dataSetRowGroups: DataSetRowGroup<T>[];
};

export function groupResultsByDataSetRow<T extends CollectionExecutionReportResultRowBase>(
  rows: T[]
): DataSetRowGroup<T>[] {
  const map = new Map<string, { dataSetRoundIndex: number | null; results: T[] }>();
  for (const row of rows) {
    const idx = row.dataSetRoundIndex;
    const key = idx == null || !Number.isFinite(Number(idx)) ? 'none' : String(Math.floor(Number(idx)));
    const roundIndex = key === 'none' ? null : Math.floor(Number(idx));
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { dataSetRoundIndex: roundIndex, results: [] };
      map.set(key, bucket);
    }
    bucket.results.push(row);
  }
  const groups = Array.from(map.values()).sort((a, b) => {
    if (a.dataSetRoundIndex == null && b.dataSetRoundIndex == null) return 0;
    if (a.dataSetRoundIndex == null) return 1;
    if (b.dataSetRoundIndex == null) return -1;
    return a.dataSetRoundIndex - b.dataSetRoundIndex;
  });
  return groups.map((g) => ({
    dataSetRoundIndex: g.dataSetRoundIndex,
    label: formatDataSetRowGroupLabel(g.dataSetRoundIndex, g.results),
    results: g.results,
    ...summarizeExecutionResultRows(g.results),
  }));
}

export function groupResultsByIteration<T extends CollectionExecutionReportResultRowBase>(
  rows: T[]
): IterationGroup<T>[] {
  const map = new Map<number, T[]>();
  for (const row of rows) {
    const iteration = row.iteration != null && Number.isFinite(Number(row.iteration))
      ? Math.max(1, Math.floor(Number(row.iteration)))
      : 1;
    let list = map.get(iteration);
    if (!list) {
      list = [];
      map.set(iteration, list);
    }
    list.push(row);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([iteration, results]) => ({
      iteration,
      results,
      ...summarizeExecutionResultRows(results),
      dataSetRowGroups: groupResultsByDataSetRow(results),
    }));
}

export type CollectionExecutionResultRenderItem<T extends CollectionExecutionReportResultRowBase> =
  | {
      kind: 'iterationHeader';
      key: string;
      iteration: number;
      total: number;
      passed: number;
      failed: number;
    }
  | {
      kind: 'dataSetRowHeader';
      key: string;
      dataSetRoundIndex: number | null;
      label: string;
      total: number;
      passed: number;
      failed: number;
    }
  | {
      kind: 'row';
      key: string;
      row: T;
      displayIndex: number;
    };

/**
 * Flatten grouped results into table render items (section headers + rows).
 * displayIndex is 1-based across the full list in view order.
 */
export function buildCollectionExecutionResultRenderItems<T extends CollectionExecutionReportResultRowBase>(
  rows: T[],
  viewMode: CollectionExecutionResultsViewMode
): CollectionExecutionResultRenderItem<T>[] {
  const items: CollectionExecutionResultRenderItem<T>[] = [];
  let displayIndex = 0;

  const pushRows = (list: T[], keyPrefix: string) => {
    for (let i = 0; i < list.length; i++) {
      const row = list[i];
      displayIndex += 1;
      items.push({
        kind: 'row',
        key: getExecutionResultRowKey(row, `${keyPrefix}-${displayIndex}`),
        row,
        displayIndex,
      });
    }
  };

  if (viewMode === 'flat' || rows.length === 0) {
    pushRows(rows, 'flat');
    return items;
  }

  if (viewMode === 'byIteration') {
    for (const g of groupResultsByIteration(rows)) {
      items.push({
        kind: 'iterationHeader',
        key: `it-h-${g.iteration}`,
        iteration: g.iteration,
        total: g.total,
        passed: g.passed,
        failed: g.failed,
      });
      for (const ds of g.dataSetRowGroups) {
        items.push({
          kind: 'dataSetRowHeader',
          key: `it-${g.iteration}-ds-${ds.dataSetRoundIndex ?? 'none'}`,
          dataSetRoundIndex: ds.dataSetRoundIndex,
          label: ds.label,
          total: ds.total,
          passed: ds.passed,
          failed: ds.failed,
        });
        pushRows(ds.results, `it-${g.iteration}-ds-${ds.dataSetRoundIndex ?? 'none'}`);
      }
    }
    return items;
  }

  // byDataSetRow
  for (const ds of groupResultsByDataSetRow(rows)) {
    items.push({
      kind: 'dataSetRowHeader',
      key: `ds-h-${ds.dataSetRoundIndex ?? 'none'}`,
      dataSetRoundIndex: ds.dataSetRoundIndex,
      label: ds.label,
      total: ds.total,
      passed: ds.passed,
      failed: ds.failed,
    });
    const byIteration = groupResultsByIteration(ds.results);
    for (const g of byIteration) {
      items.push({
        kind: 'iterationHeader',
        key: `ds-${ds.dataSetRoundIndex ?? 'none'}-it-${g.iteration}`,
        iteration: g.iteration,
        total: g.total,
        passed: g.passed,
        failed: g.failed,
      });
      pushRows(g.results, `ds-${ds.dataSetRoundIndex ?? 'none'}-it-${g.iteration}`);
    }
  }
  return items;
}

/**
 * Hide nested headers/rows under collapsed Iteration / Data set section headers.
 * Groups default to expanded when `collapsedGroupKeys` does not include the header key.
 */
export function filterCollapsedCollectionExecutionResultItems<T extends CollectionExecutionReportResultRowBase>(
  items: CollectionExecutionResultRenderItem<T>[],
  viewMode: CollectionExecutionResultsViewMode,
  collapsedGroupKeys: ReadonlySet<string> | Record<string, boolean>
): CollectionExecutionResultRenderItem<T>[] {
  const isCollapsed = (key: string) => {
    if (collapsedGroupKeys instanceof Set) return collapsedGroupKeys.has(key);
    return !!(collapsedGroupKeys as Record<string, boolean>)[key];
  };

  if (viewMode === 'flat') return items;

  const visible: CollectionExecutionResultRenderItem<T>[] = [];
  let hideUnderOuter = false;
  let hideUnderInner = false;

  for (const item of items) {
    if (viewMode === 'byIteration') {
      if (item.kind === 'iterationHeader') {
        hideUnderOuter = isCollapsed(item.key);
        hideUnderInner = false;
        visible.push(item);
        continue;
      }
      if (item.kind === 'dataSetRowHeader') {
        if (hideUnderOuter) continue;
        hideUnderInner = isCollapsed(item.key);
        visible.push(item);
        continue;
      }
      if (hideUnderOuter || hideUnderInner) continue;
      visible.push(item);
      continue;
    }

    // byDataSetRow — outer = data set, inner = iteration
    if (item.kind === 'dataSetRowHeader') {
      hideUnderOuter = isCollapsed(item.key);
      hideUnderInner = false;
      visible.push(item);
      continue;
    }
    if (item.kind === 'iterationHeader') {
      if (hideUnderOuter) continue;
      hideUnderInner = isCollapsed(item.key);
      visible.push(item);
      continue;
    }
    if (hideUnderOuter || hideUnderInner) continue;
    visible.push(item);
  }

  return visible;
}

/** Stable React key — never use rendered row index alone. */
export function getExecutionResultRowKey(
  row: CollectionExecutionReportResultRowBase & {
    request?: { id?: number | null } | null;
    attempts?: number;
  },
  fallbackPrefix = 'row'
): string {
  const requestId = row.request?.id != null ? Number(row.request.id) : 0;
  const dataSetId = row.dataSet?.id != null ? Number(row.dataSet.id) : 0;
  const iteration = row.iteration != null ? Math.floor(Number(row.iteration)) : 1;
  const attempt = row.attempts != null && Number.isFinite(Number(row.attempts)) ? Number(row.attempts) : 1;
  if (requestId > 0 && dataSetId > 0) {
    return `${requestId}-${dataSetId}-${iteration}-${attempt}`;
  }
  const order = row.dataSetOrder ?? (row.dataSetRoundIndex != null ? row.dataSetRoundIndex + 1 : 0);
  return `${fallbackPrefix}-${requestId}-o${order}-it${iteration}-a${attempt}`;
}

export function shouldShowExecutionResultsViewToggle(
  executionMode: CollectionExecutionMode | undefined,
  rows: CollectionExecutionReportResultRowBase[]
): boolean {
  if (executionMode === 'roundRobinDataSet') return true;
  const hasRoundIndex = rows.some((r) => r.dataSetRoundIndex != null);
  const iterations = new Set(rows.map((r) => r.iteration ?? 1));
  return hasRoundIndex || iterations.size > 1;
}

export function defaultExecutionResultsViewMode(
  executionMode: CollectionExecutionMode | undefined
): CollectionExecutionResultsViewMode {
  return executionMode === 'roundRobinDataSet' ? 'byIteration' : 'flat';
}

export type CollectionRequestExecutionStatus = 'not-executed' | 'pass' | 'fail' | 'partial';

/** Merge per-row / per-data-set outcomes into one Requests-table status. */
export function mergeCollectionRequestExecutionStatus(
  previous: CollectionRequestExecutionStatus | undefined,
  next: 'pass' | 'fail' | 'partial'
): 'pass' | 'fail' | 'partial' {
  if (previous == null || previous === 'not-executed') return next;
  if (previous === next) return previous;
  if (previous === 'partial' || next === 'partial') return 'partial';
  // pass vs fail across data sets / rounds → PARTIAL PASS
  return 'partial';
}

function statusFromExecutionReportRow(row: {
  success: boolean;
  assertionDetails?: {
    softFailed?: string[] | null;
    hardFailed?: string[] | null;
  } | null;
}): 'pass' | 'fail' | 'partial' {
  const softFailedCount = row.assertionDetails?.softFailed?.length ?? 0;
  const hardFailedCount = row.assertionDetails?.hardFailed?.length ?? 0;
  if (!row.success) return 'fail';
  if (softFailedCount > 0 && hardFailedCount === 0) return 'partial';
  return 'pass';
}

/**
 * Derive Requests-table Status from Complete Execution Results rows.
 * Aggregates all rows for a request (mixed pass/fail data sets → partial).
 */
export function requestStatusesFromExecutionReportResults(
  results: Array<{
    request?: { id?: number | null } | null;
    success: boolean;
    assertionDetails?: {
      softFailed?: string[] | null;
      hardFailed?: string[] | null;
    } | null;
  }>
): Record<number, 'pass' | 'fail' | 'partial'> {
  const out: Record<number, 'pass' | 'fail' | 'partial'> = {};
  for (const row of results) {
    const id = row.request?.id != null ? Number(row.request.id) : NaN;
    if (!Number.isFinite(id) || id <= 0) continue;
    out[id] = mergeCollectionRequestExecutionStatus(out[id], statusFromExecutionReportRow(row));
  }
  return out;
}

/** Shape restored from GET /collections/{id}/runs/latest (and save response). */
export type CollectionExecutionReportFromApi = {
  collectionId: number;
  collectionName: string;
  executedAt: string;
  executionMode?: CollectionExecutionMode;
  total: number;
  success: number;
  failed: number;
  stopped: boolean;
  stoppedReason?: 'user' | 'hardAssertion' | 'failurePolicy';
  iterationsConfigured?: number;
  iterationsDone?: number;
  retryConfigured?: number;
  retriesUsed?: number;
  overallDurationMs?: number;
  results: Array<{
    request: any;
    success: boolean;
    error?: string;
    iteration?: number;
    dataSetRoundIndex?: number | null;
    dataSetOrder?: number | null;
    dataSetLabel?: string | null;
    dataSet?: CollectionExecutionReportDataSetRef | null;
    attempts?: number;
    retriesUsed?: number;
    durationMs?: number;
    response?: {
      status?: number;
      statusText?: string;
      headers?: Record<string, any>;
      body?: string;
      executedAt?: string;
    };
    assertionResults?: any[];
    assertionDetails?: {
      requestLevelTotal?: number;
      dataSetLevelTotal?: number;
      softFailed?: string[];
      hardFailed?: string[];
    };
    dynamicVariables?: Array<{
      name: string;
      value: string;
      scope?: string;
      extractJsonPath?: string;
      extractHeaderName?: string;
      source?: 'jsonPath' | 'header';
      requestId?: number | null;
      requestName?: string | null;
      status?: 'extracted' | 'cleared';
    }>;
    callerRequestId?: number | null;
    callerRequestName?: string | null;
    ruleCallTiming?: 'pre_request' | 'post_response' | null;
    executionSequence?: number;
  }>;
};

function toIsoExecutedAt(value: unknown): string {
  if (value == null) return new Date().toISOString();
  const s = String(value).trim();
  if (!s) return new Date().toISOString();
  if (s.includes('T') && (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s))) return s;
  // LocalDateTime from backend: "2026-07-25T12:00:00"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return `${s}${s.endsWith('Z') ? '' : 'Z'}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

/**
 * Map backend collection run detail (save response or latest) into the in-memory report shape.
 */
export function mapCollectionRunApiToReport(run: any): CollectionExecutionReportFromApi | null {
  if (!run || typeof run !== 'object') return null;
  const collectionId = Number(run.collectionId);
  if (!Number.isFinite(collectionId) || collectionId <= 0) return null;

  const modeRaw = String(run.executionMode ?? '').trim();
  const executionMode: CollectionExecutionMode | undefined =
    modeRaw === 'roundRobinDataSet' || modeRaw === 'requestByRequest'
      ? modeRaw
      : undefined;

  const stoppedReasonRaw = String(run.stoppedReason ?? '').trim();
  const stoppedReason =
    stoppedReasonRaw === 'user' || stoppedReasonRaw === 'hardAssertion' || stoppedReasonRaw === 'failurePolicy'
      ? stoppedReasonRaw
      : undefined;

  const resultsIn = Array.isArray(run.results) ? run.results : [];
  const results = resultsIn.map((row: any) => {
    const req = row?.request && typeof row.request === 'object' ? row.request : {};
    const ds = row?.dataSet && typeof row.dataSet === 'object' ? row.dataSet : null;
    let dataSetRef: CollectionExecutionReportDataSetRef | null = null;
    if (ds) {
      const dsId = ds.id != null ? Number(ds.id) : NaN;
      const fromId = Number.isFinite(dsId) && dsId > 0 ? formatExecutionDataSetRef({ id: dsId, name: ds.name }) : null;
      if (fromId) {
        dataSetRef = fromId;
      } else if (ds.name != null && String(ds.name).trim()) {
        dataSetRef = { id: 0, name: String(ds.name).trim() };
      }
    }

    const dataSetOrderRaw = row?.dataSetOrder ?? row?.dataSet_order;
    const dataSetLabelRaw = row?.dataSetLabel ?? row?.dataSet_label ?? row?.dataSetName;
    const roundIndex =
      row?.dataSetRoundIndex != null && Number.isFinite(Number(row.dataSetRoundIndex))
        ? Number(row.dataSetRoundIndex)
        : null;
    const storedName = ds?.name != null ? String(ds.name).trim() : '';
    const isAutoName = isAutoGeneratedDataSetName(storedName);
    const dataSetOrder =
      dataSetRef != null
        ? dataSetOrderRaw != null && Number.isFinite(Number(dataSetOrderRaw))
          ? Math.max(1, Math.floor(Number(dataSetOrderRaw)))
          : roundIndex != null
          ? roundIndex + 1
          : null
        : null;
    const dataSetLabel =
      dataSetRef != null
        ? dataSetLabelRaw != null && String(dataSetLabelRaw).trim()
          ? String(dataSetLabelRaw).trim()
          : dataSetOrder != null && (isAutoName || !storedName)
          ? `Data Set ${dataSetOrder}`
          : storedName || null
        : null;

    return {
      request: {
        id: req.id != null ? Number(req.id) : undefined,
        name: req.name != null ? String(req.name) : undefined,
        method: req.method != null ? String(req.method) : undefined,
        url: req.url != null ? String(req.url) : undefined,
      },
      requestBody: row?.requestBody != null ? String(row.requestBody) : undefined,
      requestHeaders: (() => {
        const raw = row?.requestHeaders;
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
        const out: Record<string, string> = {};
        for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
          const name = String(key ?? '').trim();
          if (!name) continue;
          out[name] = value != null ? String(value) : '';
        }
        return Object.keys(out).length > 0 ? out : undefined;
      })(),
      success: row?.success === true,
      error: row?.error != null ? String(row.error) : undefined,
      iteration: row?.iteration != null ? Number(row.iteration) : undefined,
      dataSetRoundIndex: roundIndex,
      dataSetOrder,
      dataSetLabel,
      dataSet:
        dataSetRef != null
          ? { ...dataSetRef, name: dataSetLabel ?? dataSetRef.name }
          : null,
      attempts: row?.attempts != null ? Number(row.attempts) : undefined,
      retriesUsed: row?.retriesUsed != null ? Number(row.retriesUsed) : undefined,
      durationMs: row?.durationMs != null ? Number(row.durationMs) : undefined,
      response: row?.response
        ? {
            status: row.response.status != null ? Number(row.response.status) : undefined,
            statusText: row.response.statusText != null ? String(row.response.statusText) : undefined,
            headers:
              row.response.headers && typeof row.response.headers === 'object'
                ? row.response.headers
                : undefined,
            body: row.response.body != null ? String(row.response.body) : undefined,
            executedAt: row.response.executedAt != null ? toIsoExecutedAt(row.response.executedAt) : undefined,
          }
        : undefined,
      assertionResults: Array.isArray(row?.assertionResults) ? row.assertionResults : [],
      assertionDetails:
        row?.assertionDetails && typeof row.assertionDetails === 'object'
          ? {
              requestLevelTotal: row.assertionDetails.requestLevelTotal,
              dataSetLevelTotal: row.assertionDetails.dataSetLevelTotal,
              softFailed: Array.isArray(row.assertionDetails.softFailed)
                ? row.assertionDetails.softFailed.map(String)
                : [],
              hardFailed: Array.isArray(row.assertionDetails.hardFailed)
                ? row.assertionDetails.hardFailed.map(String)
                : [],
            }
          : undefined,
      callerRequestId:
        row?.callerRequestId != null && Number.isFinite(Number(row.callerRequestId))
          ? Number(row.callerRequestId)
          : null,
      callerRequestName: row?.callerRequestName != null ? String(row.callerRequestName) : null,
      ruleCallTiming:
        row?.ruleCallTiming === 'pre_request' || row?.ruleCallTiming === 'post_response'
          ? row.ruleCallTiming
          : null,
      executionSequence:
        row?.executionSequence != null && Number.isFinite(Number(row.executionSequence))
          ? Number(row.executionSequence)
          : row?.sequenceIndex != null && Number.isFinite(Number(row.sequenceIndex))
            ? Number(row.sequenceIndex)
            : undefined,
      dynamicVariables: Array.isArray(row?.dynamicVariables)
        ? row.dynamicVariables.map((dv: any) => ({
            name: String(dv?.name ?? ''),
            value: String(dv?.value ?? ''),
            scope: dv?.scope != null ? String(dv.scope) : undefined,
            extractJsonPath: dv?.extractJsonPath != null ? String(dv.extractJsonPath) : undefined,
            extractHeaderName: dv?.extractHeaderName != null ? String(dv.extractHeaderName) : undefined,
            source:
              dv?.source === 'header' || dv?.source === 'jsonPath' ? dv.source : undefined,
            requestId:
              dv?.requestId != null && Number.isFinite(Number(dv.requestId))
                ? Number(dv.requestId)
                : null,
            requestName: dv?.requestName != null ? String(dv.requestName) : null,
            status: dv?.status === 'cleared' || dv?.status === 'extracted' ? dv.status : undefined,
          }))
        : [],
    };
  });

  const nameByRequestId = new Map<number, string>();
  for (const row of results) {
    const id = row.request?.id != null ? Number(row.request.id) : NaN;
    const name = row.request?.name != null ? String(row.request.name).trim() : '';
    if (Number.isFinite(id) && id > 0 && name) nameByRequestId.set(id, name);
  }
  for (const row of results) {
    if (row.callerRequestId && !row.callerRequestName) {
      row.callerRequestName = nameByRequestId.get(row.callerRequestId) ?? null;
    }
  }

  return {
    collectionId,
    collectionName: String(run.collectionName ?? `Collection ${collectionId}`),
    executedAt: toIsoExecutedAt(run.executedAt ?? run.finishedAt ?? run.startedAt),
    executionMode,
    total: Number(run.total ?? results.length) || results.length,
    success: Number(run.success ?? results.filter((r: { success: boolean }) => r.success).length) || 0,
    failed: Number(run.failed ?? results.filter((r: { success: boolean }) => !r.success).length) || 0,
    stopped: run.stopped === true || String(run.status ?? '').toLowerCase() === 'stopped',
    stoppedReason,
    iterationsConfigured: run.iterationsConfigured != null ? Number(run.iterationsConfigured) : undefined,
    iterationsDone: run.iterationsDone != null ? Number(run.iterationsDone) : undefined,
    retryConfigured: run.retryConfigured != null ? Number(run.retryConfigured) : undefined,
    retriesUsed: run.retriesUsed != null ? Number(run.retriesUsed) : undefined,
    overallDurationMs: run.overallDurationMs != null ? Number(run.overallDurationMs) : undefined,
    results,
  };
}
